/**
 * MicrosoftGraphIntegration - Microsoft Graph API integration
 * 
 * CHANGE HISTORY:
 * - 251026_0835 [pending] danielbeer: Initial creation
 * 
 * Handles Microsoft Graph API calls with automatic OAuth2 authentication
 */

import { OAuth2TokenManager, type OAuth2Config } from '../auth/OAuth2TokenManager.js';
import type { APICallConfig, APIResponse } from '../types.js';

export interface MicrosoftGraphConfig {
  tenantId: string;
  clientId: string;
  clientSecret?: string;
  accessToken?: string; // Pre-configured token
  scopes?: string[]; // Default: ['https://graph.microsoft.com/.default']
}

/**
 * Microsoft Graph Integration
 */
export class MicrosoftGraphIntegration {
  private config: MicrosoftGraphConfig;
  private tokenManager: OAuth2TokenManager;
  private baseUrl = 'https://graph.microsoft.com/v1.0';
  private tokenKey: string;

  constructor(config: MicrosoftGraphConfig, tokenManager?: OAuth2TokenManager) {
    this.config = config;
    this.tokenManager = tokenManager || new OAuth2TokenManager();
    this.tokenKey = `microsoft-graph-${config.tenantId}-${config.clientId}`;

    // If pre-configured token provided, store it
    if (config.accessToken) {
      this.tokenManager.setToken(this.tokenKey, config.accessToken);
    }
  }

  /**
   * Make authenticated Graph API call
   */
  async call<T = any>(endpoint: string, options: Partial<APICallConfig> = {}): Promise<APIResponse<T>> {
    const startTime = Date.now();

    try {
      // Get valid access token (auto-refresh if needed)
      const accessToken = await this.getAccessToken();

      // Build full URL
      const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;

      // Make request
      const response = await fetch(url, {
        method: options.method || 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          ...options.headers,
        },
        body: options.data ? JSON.stringify(options.data) : undefined,
      });

      const responseTime = Date.now() - startTime;
      const data = response.ok ? await response.json() : null;

      return {
        success: response.ok,
        data,
        status: response.status,
        statusText: response.statusText,
        headers: this.extractHeaders(response.headers),
        responseTime,
        error: response.ok ? undefined : await response.text(),
      };
    } catch (error) {
      return {
        success: false,
        data: null as any,
        status: 0,
        statusText: 'Network Error',
        headers: {},
        responseTime: Date.now() - startTime,
        error: error.message,
      };
    }
  }

  /**
   * Get valid access token
   */
  private async getAccessToken(): Promise<string> {
    // If pre-configured token exists, use it
    const existingToken = await this.tokenManager.getTokenInfo(this.tokenKey);
    if (existingToken && this.config.accessToken) {
      return existingToken.accessToken;
    }

    // Otherwise, use OAuth2 flow
    if (!this.config.clientSecret) {
      throw new Error('Microsoft Graph: clientSecret required for OAuth2 authentication');
    }

    const oauth2Config: OAuth2Config = {
      clientId: this.config.clientId,
      clientSecret: this.config.clientSecret,
      tenantId: this.config.tenantId,
      tokenUrl: `https://login.microsoftonline.com/${this.config.tenantId}/oauth2/v2.0/token`,
      scope: this.config.scopes || ['https://graph.microsoft.com/.default'],
      grantType: 'client_credentials',
    };

    return await this.tokenManager.getToken(this.tokenKey, oauth2Config);
  }

  /**
   * Extract headers from Response
   */
  private extractHeaders(headers: Headers): Record<string, string> {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }

  /**
   * Planner-specific methods
   */
  planner = {
    /**
     * Get plan
     */
    getPlan: async (planId: string) => {
      return await this.call(`/planner/plans/${planId}`);
    },

    /**
     * Get plan tasks
     */
    getPlanTasks: async (planId: string) => {
      return await this.call(`/planner/plans/${planId}/tasks`);
    },

    /**
     * Get task
     */
    getTask: async (taskId: string) => {
      return await this.call(`/planner/tasks/${taskId}`);
    },

    /**
     * Create task
     */
    createTask: async (task) => {
      return await this.call('/planner/tasks', {
        method: 'POST',
        data: task,
      });
    },

    /**
     * Update task
     */
    updateTask: async (taskId: string, updates, etag: string) => {
      return await this.call(`/planner/tasks/${taskId}`, {
        method: 'PATCH',
        headers: {
          'If-Match': etag,
        },
        data: updates,
      });
    },

    /**
     * Delete task
     */
    deleteTask: async (taskId: string, etag: string) => {
      return await this.call(`/planner/tasks/${taskId}`, {
        method: 'DELETE',
        headers: {
          'If-Match': etag,
        },
      });
    },

    /**
     * Get plan buckets
     */
    getPlanBuckets: async (planId: string) => {
      return await this.call(`/planner/plans/${planId}/buckets`);
    },
  };

  /**
   * Project-specific methods
   */
  project = {
    /**
     * Get project
     */
    getProject: async (projectId: string) => {
      return await this.call(`/projects/${projectId}`);
    },

    /**
     * Get project tasks
     */
    getProjectTasks: async (projectId: string) => {
      return await this.call(`/projects/${projectId}/tasks`);
    },

    /**
     * Get task
     */
    getTask: async (projectId: string, taskId: string) => {
      return await this.call(`/projects/${projectId}/tasks/${taskId}`);
    },

    /**
     * Create task
     */
    createTask: async (projectId: string, task) => {
      return await this.call(`/projects/${projectId}/tasks`, {
        method: 'POST',
        data: task,
      });
    },

    /**
     * Update task
     */
    updateTask: async (projectId: string, taskId: string, updates) => {
      return await this.call(`/projects/${projectId}/tasks/${taskId}`, {
        method: 'PATCH',
        data: updates,
      });
    },

    /**
     * Delete task
     */
    deleteTask: async (projectId: string, taskId: string) => {
      return await this.call(`/projects/${projectId}/tasks/${taskId}`, {
        method: 'DELETE',
      });
    },
  };

  /**
   * Test connection
   */
  async testConnection(): Promise<boolean> {
    try {
      const response = await this.call('/me');
      return response.success;
    } catch (error) {
      console.error('Microsoft Graph connection test failed:', error);
      return false;
    }
  }

  /**
   * Clear cached token
   */
  async clearToken(): Promise<void> {
    await this.tokenManager.clearToken(this.tokenKey);
  }
}

export default MicrosoftGraphIntegration;
